import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Home,
  BarChart3,
  TrendingUp,
  Users,
  Package,
  FileText,
  ArrowRight,
  CheckCircle,
  Clock,
  DollarSign,
  Shield,
  Zap,
} from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const OverviewLanding = () => {
  const navigate = useNavigate();

  const overviewCards = [
    {
      id: 'dashboard',
      title: 'Dashboard',
      description: 'Main dashboard with key metrics and overview',
      icon: <Home size={24} />,
      path: '/dashboard',
      iconBg: '#F3F4F6',
      iconLine: '#6B7280',
      features: ['Key metrics', 'Overview charts', 'Quick actions'],
    },
    {
      id: 'analytics-overview',
      title: 'Analytics Overview',
      description: 'Comprehensive analytics and performance insights',
      icon: <BarChart3 size={24} />,
      path: '/insights/dashboards',
      iconBg: '#F3F4F6',
      iconLine: '#6B7280',
      features: ['Performance metrics', 'Trend analysis', 'KPI tracking'],
    },
    {
      id: 'asset-overview',
      title: 'Asset Overview',
      description: 'Asset management summary and key statistics',
      icon: <Package size={24} />,
      path: '/assets/management',
      iconBg: '#F3F4F6',
      iconLine: '#6B7280',
      features: ['Asset summary', 'Utilization rates', 'Status tracking'],
    },
    {
      id: 'contract-overview',
      title: 'Contract Overview',
      description: 'Contract management summary and key metrics',
      icon: <FileText size={24} />,
      path: '/contracts/management',
      iconBg: '#F3F4F6',
      iconLine: '#6B7280',
      features: ['Contract summary', 'Expiry tracking', 'Value analysis'],
    },
    {
      id: 'financial-overview',
      title: 'Financial Overview',
      description: 'Financial performance and cost analysis',
      icon: <DollarSign size={24} />,
      path: '/finance',
      iconBg: '#F3F4F6',
      iconLine: '#6B7280',
      features: ['Cost analysis', 'ROI tracking', 'Budget monitoring'],
    },
    {
      id: 'ai-insights-overview',
      title: 'AI Insights Overview',
      description: 'AI-powered insights and intelligent recommendations',
      icon: <Zap size={24} />,
      path: '/ai',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: [
        'AI insights',
        'Smart recommendations',
        'Predictive analytics',
      ],
    },
  ];

  const handleCardClick = (path) => {
    navigate(path);
  };

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader
        title="Overview & Dashboard"
        description="Comprehensive overview of your Contract LifeCycle Management system"
      />
      <div className="page-container">
        {/* Overview Management Cards */}
        <div
          className="grid-modern-3"
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            width: '100%',
          }}
        >
          {overviewCards.map((card) => (
            <div
              key={card.id}
              className="asset-landing-card"
              onClick={() => handleCardClick(card.path)}
            >
              {/* Top Accent Line */}
              <div
                style={{
                  height: '4px',
                  background: `var(--primary-color)`,
                  width: '100%',
                }}
              />

              {/* Card Content */}
              <div style={{ padding: '24px' }}>
                {/* Icon and Title */}
                <div className="d-flex align-items-center mb-3">
                  <div
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg"
                    style={{
                      background: card.iconBg,
                      marginRight: '16px',
                    }}
                  >
                    <div className="asset-card-icon" style={{ color: card.iconLine }}>
                      {React.cloneElement(card.icon, {
                        size: 20
                      })}
                    </div>
                  </div>
                  <div className="flex-grow-1">
                    <h4
                      style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: 'var(--text-primary)',
                        margin: '0',
                        fontFamily: 'var(--font-family)',
                      }}
                    >
                      {card.title}
                    </h4>
                  </div>
                  <ArrowRight size={20} className="asset-card-arrow" />
                </div>

                {/* Description */}
                <p
                  style={{
                    fontSize: '14px',
                    color: 'var(--text-secondary)',
                    margin: '0 0 16px 0',
                    lineHeight: '1.5',
                    fontFamily: 'var(--font-family)',
                  }}
                >
                  {card.description}
                </p>

                {/* Features */}
                <div>
                  <div
                    style={{
                      fontSize: '12px',
                      fontWeight: '500',
                      color: '#374151',
                      marginBottom: '8px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Key Features
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    {card.features.map((feature, index) => (
                      <span
                        key={index}
                        style={{
                          fontSize: '12px',
                          padding: '4px 8px',
                          background: '#f3f4f6',
                          color: '#374151',
                          borderRadius: '6px',
                          fontWeight: '500',
                        }}
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Information */}
        {/* <div
          className=""
          style={{
            background: '#ffffff',
            borderRadius: '12px',
            padding: '32px',
            margin: '24px 0',
            border: '1px solid #e5e7eb',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
          }}
        >
          <div className="">
            <div className="">
              <h3
                style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  color: '#1f2937',
                  marginBottom: '24px',
                  fontFamily: 'var(--font-family)',
                  paddingBottom: '16px',
                  borderBottom: '2px solid #f3f4f6',
                }}
              >
                System Overview
              </h3>
            </div>
            <div className="">
              <div
                className="grid-modern-2"
                style={{
                  gap: '24px',
                }}
              >
                <div
                  style={{
                    background: '#f9fafb',
                    borderRadius: '10px',
                    padding: '20px',
                    border: '1px solid #e5e7eb',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow =
                      '0 4px 12px rgba(0, 0, 0, 0.08)';
                    e.currentTarget.style.borderColor = '#d1d5db';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = '#e5e7eb';
                  }}
                >
                  <h5
                    style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#1f2937',
                      marginBottom: '12px',
                      fontFamily: 'var(--font-family)',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <CheckCircle
                      size={20}
                      style={{
                        color: '#10B981',
                        marginRight: '8px',
                        flexShrink: 0,
                      }}
                    />
                    Comprehensive Overview
                  </h5>
                  <p
                    style={{
                      fontSize: '14px',
                      color: '#6b7280',
                      margin: '0',
                      lineHeight: '1.6',
                    }}
                  >
                    Get a complete overview of your contract and asset
                    management system with key metrics, performance indicators,
                    and intelligent insights.
                  </p>
                </div>

                <div
                  style={{
                    background: '#f9fafb',
                    borderRadius: '10px',
                    padding: '20px',
                    border: '1px solid #e5e7eb',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow =
                      '0 4px 12px rgba(0, 0, 0, 0.08)';
                    e.currentTarget.style.borderColor = '#d1d5db';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = '#e5e7eb';
                  }}
                >
                  <h5
                    style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#1f2937',
                      marginBottom: '12px',
                      fontFamily: 'var(--font-family)',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <TrendingUp
                      size={20}
                      style={{
                        color: 'var(--gold-primary)',
                        marginRight: '8px',
                        flexShrink: 0,
                      }}
                    />
                    Real-Time Analytics
                  </h5>
                  <p
                    style={{
                      fontSize: '14px',
                      color: '#6b7280',
                      margin: '0',
                      lineHeight: '1.6',
                    }}
                  >
                    Access real-time analytics, performance dashboards, and
                    AI-powered insights to make data-driven decisions and
                    optimize your operations.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div> */}
      </div>
    </>
  );
};

export default OverviewLanding;
